/**
 * Data Sanitizer - Prepare raw user data for LLM inference
 *
 * Responsibilities:
 * 1. Filter data by time window
 * 2. Remove PII (emails, phone numbers, addresses)
 * 3. Summarize/truncate to fit context limits
 * 4. Format data for LLM consumption
 *
 * @see docs/sprints/ownyou-sprint6-spec.md
 * @see docs/architecture/OwnYou_architecture_v13.md Section 2.4
 */
const DEFAULT_CONFIG = {
    dataWindowDays: 90,
    maxItemsPerCategory: 50,
    maxCharsPerItem: 200,
    maxTotalChars: 8000,
};
/**
 * PII patterns to remove from text
 */
const PII_PATTERNS = {
    // Email addresses
    email: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
    // Phone numbers (3-3-4 digit groups, optional country code)
    phone: /(?:\+\d{1,3}[-.\s])?\(?\d{3}\)?[-.\s]\d{3}[-.\s]\d{4}/g,
    // Credit card numbers
    creditCard: /\b\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}\b/g,
    // SSN
    ssn: /\b\d{3}[-\s]?\d{2}[-\s]?\d{4}\b/g,
    // IP addresses
    ip: /\b(?:\d{1,3}\.){3}\d{1,3}\b/g,
    // Street addresses (simplified)
    address: /\b\d{1,5}\s+[A-Za-z]+\s+(?:St|Street|Ave|Avenue|Blvd|Boulevard|Rd|Road|Dr|Drive|Ln|Lane|Way|Ct|Court|Pl|Place)\b/gi,
};
/**
 * Sanitize user data for LLM consumption
 *
 * @param rawData - Raw user data bundle from Store
 * @param dataWindowDays - Number of days of data to include
 * @returns Sanitized string for LLM prompt
 */
export async function sanitizeDataForLLM(rawData, dataWindowDays = 90) {
    const config = {
        ...DEFAULT_CONFIG,
        dataWindowDays,
    };
    const cutoffDate = Date.now() - dataWindowDays * 24 * 60 * 60 * 1000;
    const sections = [];
    // Process IAB classifications
    if (rawData.iabClassifications.length > 0) {
        const iabSection = processIABClassifications(rawData.iabClassifications, cutoffDate, config);
        if (iabSection) {
            sections.push(iabSection);
        }
    }
    // Process emails
    if (rawData.emails.length > 0) {
        const emailSection = processEmails(rawData.emails, cutoffDate, config);
        if (emailSection) {
            sections.push(emailSection);
        }
    }
    // Process financial data (if available)
    if (rawData.financial && rawData.financial.length > 0) {
        const financialSection = processFinancial(rawData.financial, cutoffDate, config);
        if (financialSection) {
            sections.push(financialSection);
        }
    }
    // Process calendar data (if available)
    if (rawData.calendar && rawData.calendar.length > 0) {
        const calendarSection = processCalendar(rawData.calendar, cutoffDate, config);
        if (calendarSection) {
            sections.push(calendarSection);
        }
    }
    // Combine and truncate if needed
    let result = sections.join('\n\n');
    if (result.length > config.maxTotalChars) {
        result = result.substring(0, config.maxTotalChars) + '\n[Data truncated]';
    }
    return result || 'No data available for the specified time window.';
}
/**
 * Process IAB classification data
 */
function processIABClassifications(items, cutoffDate, config) {
    const filtered = filterByDate(items, cutoffDate);
    if (filtered.length === 0) {
        return null;
    }
    const limited = filtered.slice(0, config.maxItemsPerCategory);
    const summaries = limited.map((item) => {
        const val = item.value;
        const category = val.taxonomy_id || val.category || 'Unknown';
        const confidence = val.confidence ?? 0;
        const source = val.source || 'email';
        const summary = val.summary ? truncate(String(val.summary), config.maxCharsPerItem) : '';
        return `- [${category}] (${Math.round(Number(confidence) * 100)}% confidence) ${summary}`.trim();
    });
    return `## User Interests (IAB Classifications)\n${summaries.join('\n')}`;
}
/**
 * Process email data - extract subject lines and summaries
 */
function processEmails(items, cutoffDate, config) {
    const filtered = filterByDate(items, cutoffDate);
    if (filtered.length === 0) {
        return null;
    }
    const limited = filtered.slice(0, config.maxItemsPerCategory);
    const summaries = limited.map((item) => {
        const val = item.value;
        const subject = sanitizePII(String(val.subject || 'No subject'));
        const sender = val.sender_name
            ? sanitizePII(String(val.sender_name))
            : 'Unknown';
        const date = formatDate(val.date);
        const summary = val.summary
            ? sanitizePII(truncate(String(val.summary), config.maxCharsPerItem))
            : '';
        return `- [${date}] From: ${sender} - "${subject}"${summary ? ` | ${summary}` : ''}`;
    });
    return `## Email Activity\n${summaries.join('\n')}`;
}
/**
 * Process financial/transaction data
 */
function processFinancial(items, cutoffDate, config) {
    const filtered = filterByDate(items, cutoffDate);
    if (filtered.length === 0) {
        return null;
    }
    const limited = filtered.slice(0, config.maxItemsPerCategory);
    const summaries = limited.map((item) => {
        const val = item.value;
        const merchant = sanitizePII(String(val.merchant || 'Unknown'));
        const category = val.category || 'Uncategorized';
        const amount = val.amount ? `$${Number(val.amount).toFixed(2)}` : '';
        const date = formatDate(val.date);
        return `- [${date}] ${merchant} (${category})${amount ? ` - ${amount}` : ''}`;
    });
    return `## Transactions\n${summaries.join('\n')}`;
}
/**
 * Process calendar data
 */
function processCalendar(items, cutoffDate, config) {
    const filtered = filterByDate(items, cutoffDate);
    if (filtered.length === 0) {
        return null;
    }
    const limited = filtered.slice(0, config.maxItemsPerCategory);
    const summaries = limited.map((item) => {
        const val = item.value;
        const title = sanitizePII(String(val.title || 'Untitled'));
        const date = formatDate(val.date);
        const attendees = val.attendees
            ? ` with ${val.attendees.length} attendees`
            : '';
        const location = val.location
            ? ` at ${sanitizePII(String(val.location))}`
            : '';
        return `- [${date}] ${title}${attendees}${location}`;
    });
    return `## Calendar Events\n${summaries.join('\n')}`;
}
/**
 * Filter items by date cutoff
 */
function filterByDate(items, cutoffDate) {
    return items.filter((item) => {
        const val = item.value;
        const dateValue = val.date || val.created_at || val.timestamp || val.received_at;
        if (!dateValue) {
            return true; // Include items without dates
        }
        const timestamp = typeof dateValue === 'number'
            ? dateValue
            : new Date(String(dateValue)).getTime();
        return timestamp >= cutoffDate;
    });
}
/**
 * Remove PII from text
 */
export function sanitizePII(text) {
    let result = text;
    for (const pattern of Object.values(PII_PATTERNS)) {
        result = result.replace(pattern, '[REDACTED]');
    }
    return result;
}
/**
 * Truncate text to max length
 */
function truncate(text, maxLength) {
    if (text.length <= maxLength) {
        return text;
    }
    return text.substring(0, maxLength - 3) + '...';
}
/**
 * Format date for display
 */
function formatDate(date) {
    if (!date) {
        return 'Unknown date';
    }
    const timestamp = typeof date === 'number' ? date : new Date(date).getTime();
    const d = new Date(timestamp);
    return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
}
/**
 * Generate a person label for anonymization.
 * 0-25 → A-Z, 26+ → AA, AB, AC, ...
 */
function personLabel(index) {
    if (index < 26)
        return String.fromCharCode(65 + index);
    return String.fromCharCode(65 + Math.floor(index / 26) - 1) + String.fromCharCode(65 + (index % 26));
}
/**
 * Sanitize pre-aggregated data based on privacy tier before sending to LLM.
 *
 * Tiers:
 * - local: No sanitization (data stays on device)
 * - cloud-zdr: PII scrubbed, amounts rounded, first names only
 * - cloud-standard: PII scrubbed, amounts bucketed, names anonymized
 */
export function sanitizeForLLM(aggregatedData, privacyTier, _source) {
    if (!aggregatedData)
        return '';
    // Local models: no sanitization needed
    if (privacyTier === 'local')
        return aggregatedData;
    // Both cloud tiers get PII scrubbing
    let result = sanitizePII(aggregatedData);
    if (privacyTier === 'cloud-standard') {
        // Bucket dollar amounts: $42.17 → "$40-50"
        result = result.replace(/\$(\d+)(?:\.\d+)?/g, (_, amount) => {
            const n = parseInt(amount, 10);
            const lower = Math.floor(n / 10) * 10;
            const upper = lower + 10;
            return `$${lower}-${upper}`;
        });
        // Anonymize email sender names to "Person A", "Person B", etc.
        let personIndex = 0;
        const personMap = new Map();
        result = result.replace(/^([A-Za-z][a-z.]+(?:\s[A-Za-z][a-z.]+)?): /gm, (match, name) => {
            // Skip domain-like names (e.g., "amazon.com")
            if (name.includes('.') && !name.includes(' '))
                return match;
            if (!personMap.has(name)) {
                personMap.set(name, `Person ${personLabel(personIndex++)}`);
            }
            return `${personMap.get(name)}: `;
        });
        // Strip calendar locations for cloud-standard
        result = result.replace(/(?:at|Location:)\s+[^\n,;]+/gi, '[location redacted]');
        // Reduce URLs to domain-only for cloud-standard
        result = result.replace(/https?:\/\/([^\/\s]+)[^\s]*/g, '$1');
    }
    else if (privacyTier === 'cloud-zdr') {
        // Round dollar amounts: $42.17 → "$42"
        result = result.replace(/\$(\d+)\.\d+/g, (_, amount) => `$${amount}`);
        // Reduce sender names to first name only for cloud-zdr
        result = result.replace(/^([A-Z][a-z]+)\s+[A-Z][a-z]+(?:\s[A-Z][a-z]+)*:/gm, '$1:');
    }
    return result;
}
//# sourceMappingURL=data-sanitizer.js.map